import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';

import { getAbPlanData } from '../../abtest';
import { PATH_CHECKOUT } from '../../constants';
import connectExtension, {
  getExtensionLanding,
} from '../../extension/Provider';
import { log } from '../../logger';
import { asAbsoluteUrl } from '../../utils/url-util';

import { Button, CardSection, FormBody, PrimaryButton } from '../Base';
import Dollars from '../Dollars';
import Emojis, { TYPE_PREMIUM } from '../Emojis';
import { connectGTM } from '../GTMListener';
import LoaderDots from '../LoaderDots';

import { asErrorMsg } from '../../api';
import CheckoutHeader from './CheckoutHeader';
import CheckoutStage from './CheckoutStage';
import StageButtons from './StageButtons';
import { QS_CHECKOUT_LOOKUP_KEY } from './utils';

// Implemented in CheckoutStages/index.js
const PropTypeStage = PropTypes.shape({
  slug: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  component: PropTypes.elementType.isRequired,
  nextIsLast: PropTypes.bool,
  logAb: PropTypes.func.isRequired,
});

class CheckoutPayment extends CheckoutStage {
  static propTypes = {
    account: PropTypes.object,
    extensionIsConnected: PropTypes.bool.isRequired,
    prevStage: PropTypeStage,
    nextStage: PropTypeStage,
    goToStage: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    dataLayerPush: PropTypes.func.isRequired,
    alreadyHasSubscription: PropTypes.bool.isRequired,
    createCheckoutSessionUrl: PropTypes.func.isRequired,
    performSubscriptionUpgrade: PropTypes.func.isRequired,
    isStripeCheckout: PropTypes.bool,
    urlPathPrefix: PropTypes.string.isRequired,
    lookupKey: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      isSubmitting: false,
      HACKdoCheckout: false, // cruft from testing forced checkouts
      taxPercent: 0,
      isCalculatingTax: false,
      showTaxDetail: false,
      stripeCheckoutErrorMsg: '',
      upgradeData: null, // get from resp.type === "upgrade"
      upgradeAction: null, // { type: 'loading' } | { type: 'error'; msg: string; } | null
    };
    this.plan = getAbPlanData();
    this._mounted = true;

    this.postalCode = '';
    this.postalCodeToRate = {};

    this.postTrialRef = React.createRef();
  }

  // life-cycle
  componentDidMount() {}

  componentWillUnmount() {
    this._mounted = false;
  }

  // handlers
  handleStripeCheckout = () => {
    log('[CheckoutPayment.handleStripeCheckout]');
    this.setState({ stripeCheckoutErrorMsg: '' });

    const checkoutParams = this._getCheckoutUrls();

    return this.props
      .createCheckoutSessionUrl(checkoutParams)
      .then((_resp) => {
        /** @type {import('../../api').CheckoutResponse} */
        const resp = _resp;
        switch (resp.type) {
          case 'url': {
            const { url } = resp;
            if (this._mounted) {
              window.location.href = url;
            }

            break;
          }
          case 'upgrade': {
            this.setState({
              upgradeData: resp,
            });
            break;
          }
          default: {
            const msg =
              resp.error || `Unexpected error: ${JSON.stringify(resp)}`;
            this.setState({ stripeCheckoutErrorMsg: msg });
            break;
          }
        }
      })
      .catch((err) => {
        this.setState({ stripeCheckoutErrorMsg: asErrorMsg(err) });
      });
  };

  /**
   * Upgrade an existing subscription to a
   * new price.
   *
   * @param {{
   *   priceId: string;
   *   subscriptionId: string;
   * }} opts
   */
  handleStripeUpgradeSub = () => {
    if (this.state.upgradeAction?.type === 'loading') {
      log.warn('Already loading...');
      return;
    }
    this.setState({
      upgradeAction: { type: 'loading' },
    });

    const checkoutParams = this._getCheckoutUrls();

    this.props
      .performSubscriptionUpgrade(checkoutParams)
      .then((_resp) => {
        // /** @type {import('../../types/Account').Account} */
        // const resp = _resp;
        this.props.goToStage(this.props.nextStage);
      })
      .catch((err) => {
        this.setState({
          upgradeAction: { type: 'error', msg: asErrorMsg(err) },
        });
      });
  };

  // helpers

  _getCheckoutUrls() {
    const urlPathPrefx = this.props.urlPathPrefix || PATH_CHECKOUT;

    const lookup_key = this.props.lookupKey || '';

    const extraCheckoutParams = lookup_key
      ? { [QS_CHECKOUT_LOOKUP_KEY]: lookup_key }
      : undefined;

    const cancel_url = asAbsoluteUrl(
      urlPathPrefx +
        (this.props.prevStage ? `/${this.props.prevStage.slug}` : ''),
      extraCheckoutParams,
    );
    const success_url = asAbsoluteUrl(
      urlPathPrefx +
        (this.props.nextStage ? `/${this.props.nextStage.slug}` : '/success'),
      extraCheckoutParams,
    );

    // http://localhost:1234/checkout/account?checkout=cancel&price=enterprise_5

    const checkoutParams = {
      plan: this.plan.planId,
      cancel_url,
      success_url,
    };

    if (lookup_key) {
      checkoutParams.lookup_key = lookup_key;
    }

    const hash = window.location.hash.substring(1);

    switch (hash) {
      // add '#test' to do a 5 minute trial
      case 'test': {
        checkoutParams.quick_trial = true;
        break;
      }
      // add '#limit' to force no trial
      case 'limit': {
        checkoutParams.limit = true;
        break;
      }
      // add '#nolimit' to allow trial
      case 'nolimit': {
        checkoutParams.no_limit = true;
        break;
      }
    }

    return checkoutParams;
  }

  // render
  _buttonElt(nextProps) {
    const { isSubmitting } = this.state;
    return (
      <StageButtons
        {...this.props}
        nextProps={nextProps}
        nextStage={nextProps ? this.props.nextStage : null}
        isSubmitting={isSubmitting}
      />
    );
  }

  _loadingElt() {
    return (
      <>
        <FormBody>
          <LoaderDots />
        </FormBody>
        {this._buttonElt()}
      </>
    );
  }

  showComponent() {
    const { account, extensionOrigins, lookupKey } = this.props;

    const nextUrl = this.props.nextUrl || getExtensionLanding(extensionOrigins);

    const hadPreviousTrial = false;

    let mainElt;
    if (hadPreviousTrial) {
      //
      // TODO(trial) - read started trial (so we can block repeats) and show checkout without trial
    } else if (!account) {
      // loading...
      mainElt = this._loadingElt();
    } else if (this.state.stripeCheckoutErrorMsg) {
      mainElt = (
        <>
          <FormBody>
            <Emojis type={TYPE_PREMIUM} space={true} />
            <div className="text-center">
              <p>Unable to complete checkout:</p>
              <p className="mb-6 border-0 border-l-4 border-solid border-l-gray-300 bg-gray-100 py-2 pl-4 text-left text-sm italic">
                {this.state.stripeCheckoutErrorMsg}
              </p>
              <div className="flex items-center justify-center gap-4">
                <PrimaryButton
                  className="btn-standout btn-convert"
                  type="link"
                  href={nextUrl}
                  size="small"
                >
                  Go to editor
                </PrimaryButton>
                <PrimaryButton
                  type={Button.TYPE_ROUTE}
                  to="/account"
                  className="btn-standout"
                  size="small"
                >
                  Go to account
                </PrimaryButton>
              </div>
            </div>
          </FormBody>
          {this._buttonElt()}
        </>
      );
    } else if (this.state.upgradeData) {
      const { upgradeData, upgradeAction } = this.state;
      const { subscription_name, price_name, preview_invoice } = upgradeData;

      // Determine preview amounts
      const { subtotal_excluding_tax: subtotal, total } = preview_invoice;

      const tax_total = preview_invoice.total_tax_amounts.reduce(
        (state, tax) => state + tax.amount,
        0,
      );
      const discount_total = preview_invoice.total_discount_amounts.reduce(
        (state, dis) => state - dis.amount,
        0,
      );
      const lines = [{ title: 'Subtotal', cents: subtotal }];
      if (tax_total) {
        lines.push({ title: 'Tax', cents: tax_total });
      }
      if (discount_total) {
        lines.push({ title: 'Discount', cents: discount_total });
      }
      lines.push({ title: 'Total', cents: total });
      const now = DateTime.now();
      let previewDate = DateTime.fromSeconds(preview_invoice.created);
      if (previewDate < now) {
        previewDate = now;
      }

      mainElt = (
        <>
          <FormBody>
            {upgradeAction?.type === 'error' && (
              <p className="error">{upgradeAction.msg || 'Unknown error'}</p>
            )}
            <p className="text-left">
              You have an existing subscription to{' '}
              <strong>{subscription_name}</strong>.<br />
              <br />
              Select “Upgrade now” to upgrade this account and become the
              account administrator for <strong>{price_name}</strong>.
            </p>
            <div className="pb-5 text-left text-sm text-gray-500">
              <div>
                Next charge on:{' '}
                {previewDate.toLocaleString(DateTime.DATETIME_SHORT)}
              </div>
              {lines.map(({ title, cents }) => (
                <div key={title}>
                  {title}: <Dollars cents={cents} />
                </div>
              ))}
            </div>
            {/* `-ml-5` to offset the _buttonElt */}
            {this.state.upgradeAction?.type === 'loading' ? (
              <LoaderDots />
            ) : (
              <div className="-ml-5 flex flex-row-reverse items-center justify-between">
                <PrimaryButton
                  className="btn-convert" // btn-standout
                  type="link"
                  href="#"
                  size="medium"
                  onClick={(evt) => {
                    evt.preventDefault();
                    this.handleStripeUpgradeSub();
                  }}
                >
                  Upgrade now
                </PrimaryButton>
                {this._buttonElt()}
              </div>
            )}
          </FormBody>
        </>
      );
    } else {
      // previously this was `if (this.props.isStripeCheckout) { ... }`
      // but now we always do the hosted checkout
      this.doOnce('handleStripeCheckout', this.handleStripeCheckout, true);
      mainElt = this._loadingElt();
    }

    return (
      <div className="checkoutdetails">
        <CheckoutHeader
          title="Enter billing information"
          lookupKey={lookupKey}
          lookupPrice={account?.lookupPrice}
        />
        <CardSection className="checkoutdetails">{mainElt}</CardSection>
      </div>
    );
  }
}

export default connectExtension(connectGTM(CheckoutPayment));
